'use client';

import {
  ControlType,
  CalculatorId,
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type SingleColumnSelectControl,
  type SelectControl,
  type ControlValues,
  type CalculatorTypes
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { ConfidenceIntervalCalculator, type ConfidenceIntervalResult } from './confidence-interval-calculator';
import { calculateConfidenceInterval } from '../../../lib/stats';

// Type for confidence interval specific control values
export interface ConfidenceIntervalControlValues extends ControlValues {
  [ControlIds.DataColumn]: string;
  [ControlIds.Alpha]: string;
}

const dataColumn = {
  id: ControlIds.DataColumn,
  type: ControlType.SingleColumnSelect,
  label: 'Data column',
  required: true,
  numericOnly: true
} satisfies SingleColumnSelectControl;

const confidenceLevel = {
  id: ControlIds.Alpha,
  type: ControlType.Select,
  label: 'Confidence level',
  required: true,
  options: [
    { value: '0.90', label: '90%' },
    { value: '0.95', label: '95%' },
    { value: '0.99', label: '99%' }
  ]
} satisfies SelectControl;

export const confidenceIntervalControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [dataColumn, confidenceLevel] as const
    }
  ]
};

export const confidenceIntervalConfig: CalculatorConfig<CalculatorId.CONFIDENCE_INTERVAL> = {
  id: CalculatorId.CONFIDENCE_INTERVAL,
  type: CalculatorType.Graph,
  name: 'Confidence Interval Calculator',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      {
        id: 'sample',
        name: 'Sample Data',
        values: ['23', '25', '28', '24', '26', '27', '25', '24', '26', '25']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.DataColumn]: 'sample',
    [ControlIds.Alpha]: '0.95'
  },
  controls: confidenceIntervalControls,
  getInitialConfig: (data: TableDataJSON) => {
    // Find the first numeric column
    const numericColumn = data.columns.find(col => 
      col.values.every(val => !isNaN(Number(val)))
    )?.id;

    return {
      [ControlIds.DataColumn]: numericColumn || '',
      [ControlIds.Alpha]: '0.95'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const ciControls = controls as ConfidenceIntervalControlValues;
    const dataColumn = ciControls[ControlIds.DataColumn];
    const confidenceLevel = parseFloat(ciControls[ControlIds.Alpha]);
    
    if (!dataColumn) {
      return null;
    }

    const col = data.columns.find(col => col.id === dataColumn);
    if (!col) return null;
    const numericData = col.values.map(Number);
    if (numericData.some(isNaN)) return null;

    const result = calculateConfidenceInterval(numericData, confidenceLevel);
    return result;
  },
  render: ({ visualizationData }) => <ConfidenceIntervalCalculator visualizationData={visualizationData} />
}; 